import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getDesignReviewRequest,
  getDesignFolderTree,
  uploadDesignDoc,
  uploadDesignDocVersion,
} from "../../api/design";
import Tree from "rc-tree";
import "rc-tree/assets/index.css";
import ApprovalLine_view from "../../components/approval/ApprovalLine_view";
import ApprovalAction from "../../components/approval/ApprovalAction";

export default function DesignReviewDetail() {
  const { id: projectId, dns } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // 폴더 트리 상태
  const [treeData, setTreeData] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [selectedFolderName, setSelectedFolderName] = useState("");
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [selectedDocId, setSelectedDocId] = useState(null);

  // 라이브러리 저장 패널 표시 여부
  const [showLibrary, setShowLibrary] = useState(false);

  // 업로드 입력 상태
  const [version, setVersion] = useState("");
  const [uploadComment, setUploadComment] = useState("");

  // -------------------
  // 검토 요청 상세 불러오기
  // -------------------
  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        setError("");
        const res = await getDesignReviewRequest(dns);
        setData(res.data);
      } catch (err) {
        setError("데이터를 불러오지 못했습니다.");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [dns]);

  // -------------------
  // 폴더 트리 불러오기
  // -------------------
  const loadTree = async () => {
    try {
      const res = await getDesignFolderTree(projectId);
      const formatNode = (node, parentPath = "") => {
        const currentPath = parentPath
          ? `${parentPath} / ${node.name}`
          : node.name;
        return {
          key: node.isLeaf ? `doc-${node.id}` : `folder-${node.id}`,
          title: node.name,
          isLeaf: node.isLeaf || false,
          path: currentPath,
          children: node.children
            ? node.children.map((c) => formatNode(c, currentPath))
            : [],
        };
      };
      setTreeData(res.data.map((n) => formatNode(n)));
    } catch (err) {
      console.error("폴더 트리 불러오기 실패:", err);
    }
  };

  useEffect(() => {
    loadTree();
  }, [projectId]);

  // -------------------
  // 폴더/문서 선택
  // -------------------
  const handleSelect = (keys, info) => {
    const key = info.node.key;
    if (key.startsWith("folder-")) {
      setSelectedFolderId(key.replace("folder-", ""));
      setSelectedFolderName(info.node.path || info.node.title);
      setSelectedDoc(null);
      setSelectedDocId(null);
    } else if (key.startsWith("doc-")) {
      setSelectedDoc(info.node.title);
      setSelectedDocId(key.replace("doc-", ""));
      setSelectedFolderId(null);
      setSelectedFolderName("");
    }
  };

  const renderTitle = (node) => (
    <div className="flex items-center gap-2">
      <span className="text-sm">{node.title}</span>
    </div>
  );

  // -------------------
  // 라이브러리에 저장
  // -------------------
  const handleSaveToLibrary = async () => {
    if (!data || !data.attachment) {
      alert("문서 데이터가 올바르지 않습니다. 다시 시도해주세요.");
      return;
    }
    if (!version) {
      alert("버전을 입력하세요!");
      return;
    }

    try {
      const response = await fetch(data.attachment);
      const blob = await response.blob();
      const filename = data.attachment.split("/").pop() || "document";
      const fileObj = new File([blob], filename, { type: blob.type });

      const formData = new FormData();
      formData.append("file", fileObj);
      formData.append("version", version);
      formData.append("comment", uploadComment);

      if (selectedDocId) {
        await uploadDesignDocVersion(selectedDocId, formData);
        alert("기존 문서에 새 버전으로 저장 완료!");
      } else if (selectedFolderId) {
        formData.append("project", projectId);
        formData.append("folder", selectedFolderId);
        formData.append("name", data.title);
        formData.append("discipline", data.discipline);
        formData.append("category", data.category);

        await uploadDesignDoc(formData);
        alert("새 문서로 저장 완료!");
      } else {
        alert("폴더나 문서를 선택하세요!");
        return;
      }

      setShowLibrary(false);
      setVersion("");
      setUploadComment("");
    } catch (err) {
      console.error(err);
      alert("저장 실패");
    }
  };

  if (loading) {
    return (
      <div className="mobile-page-root mb-16">
        <div className="mobile-page-root-inner space-y-3 py-6">
          <div className="bg-white rounded-lg p-6 border text-center">
            <p className="text-sm text-gray-500">로딩 중...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mobile-page-root mb-16">
        <div className="mobile-page-root-inner space-y-3 py-6">
          <div className="bg-white rounded-lg p-6 border text-center">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="mobile-page-root mb-16">
      <div className="mobile-page-root-inner space-y-3">
        {/* 문서 정보 */}
        <div className="rounded-lg border bg-white p-3 space-y-2">
          <span className="block text-base font-semibold text-gray-800">
            {data.title}
          </span>
          <div className="text-xs text-gray-600 space-y-1">
            <div>카테고리 : {data.category_display || data.category}</div>
            <div>공종 : {data.discipline_display || data.discipline}</div>
            <div>
              요청자 : {data.created_by?.last_name}
              {data.created_by?.first_name}
            </div>
            <div>
              등록일 : {new Date(data.created_at).toLocaleDateString()}
            </div>
          </div>
          <div className="text-xs">
            <span className="font-medium text-gray-600">상태 : </span>
            <span>{data.status_display || data.status}</span>
          </div>
        </div>

        {/* 검토 의견 + 첨부파일 */}
        <div className="rounded-lg border bg-white p-3">
          <div className="mb-3">
            <span className="block mb-1 text-sm font-semibold text-gray-900">
              검토 의견
            </span>
            <p className="whitespace-pre-line text-sm text-gray-700">
              {data.comment || "등록된 의견이 없습니다."}
            </p>
          </div>

          <div className="border-t border-gray-100 pt-3">
            <span className="block mb-1 text-sm font-semibold text-gray-900">
              첨부 파일
            </span>
            {data.attachment ? (
              <a
                href={data.attachment}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-blue-600 hover:underline"
              >
                파일 다운로드
              </a>
            ) : (
              <p className="text-xs text-gray-500">첨부된 파일이 없습니다.</p>
            )}
          </div>
        </div>

        {/* 승인 이력 */}
        <div className="rounded-lg border bg-white p-3">
          <span className="block mb-2 text-sm font-semibold text-gray-900">
            승인 이력
          </span>
          <ApprovalLine_view lines={data.approval_lines || []} />
        </div>

        {/* 결재 액션 */}
        <div className="rounded-lg border bg-white p-3">
          <ApprovalAction approvalLines={data.approval_lines || []} />
        </div>

        {/* 승인 상태일 때만 버튼 노출 */}
        {data.status === "approved" && !showLibrary && (
          <button
            onClick={() => setShowLibrary(true)}
            className="btn-save w-full"
          >
            라이브러리에 추가
          </button>
        )}

        {/* 라이브러리 저장 패널 */}
        {showLibrary && (
          <div className="rounded-lg border bg-white p-3 space-y-3">
            <span className="text-sm font-semibold text-gray-900">
              저장 위치 선택
            </span>
            <div className="max-h-60 overflow-auto border rounded p-2">
              <Tree
                treeData={treeData}
                expandedKeys={expandedKeys}
                onExpand={(keys) => setExpandedKeys(keys)}
                onSelect={handleSelect}
                showIcon={false}
                titleRender={renderTitle}
                className="text-slate-700"
              />
            </div>

            <div className="text-xs text-slate-600 space-y-1">
              {selectedFolderName && (
                <div>
                  선택된 폴더:{" "}
                  <span className="font-medium">{selectedFolderName}</span>
                </div>
              )}
              {selectedDoc && (
                <div>
                  선택된 문서: <span className="font-medium">{selectedDoc}</span>
                </div>
              )}
            </div>

            <input
              type="text"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="버전 (예: v1.0)"
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />

            <textarea
              value={uploadComment}
              onChange={(e) => setUploadComment(e.target.value)}
              placeholder="업로드 코멘트"
              className="w-full border rounded-lg px-3 py-2 text-sm"
              rows={3}
            />

            <div className="flex gap-2">
              <button
                onClick={() => setShowLibrary(false)}
                className="btn-cancel flex-1"
              >
                취소
              </button>
              <button
                onClick={handleSaveToLibrary}
                className="btn-save flex-1"
              >
                저장
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
